import { useCallback, useMemo } from 'react';
import sortParam from 'utils/old/sortParam';
import useUrlParamState from './useUrlParamState';

/**
 * Table sort state mirrored into a query param, so a refresh — or a shared link — restores the sort
 * the user chose instead of snapping back to the default.
 *
 * The param holds exactly what the API receives (`name` / `-name`, the DRF `ordering` convention),
 * so the URL reads the way the request does and there is no second encoding to keep in sync.
 *
 *   const [sortOption, setSortOption] = useSortOptionParam({
 *     fields: CAMPAIGN_RETAILERS_ORDERING,
 *     defaultSortBy: 'name',
 *     defaultSortOrder: 1,
 *   });
 *
 * `fields` is the ordering params this endpoint accepts; both directions of each are whitelisted and
 * anything else in the URL falls back to the default. That is what lets sibling tabs on one URL share
 * the param: a value belonging to the other tab's endpoint simply isn't valid here, so each tab
 * lands on its own default rather than sending an ordering its API would reject.
 *
 * Returns [sortOption, setSortOption] where setSortOption(sortBy, sortOrder) matches the
 * onSort(field, order) signature the sort controls already call.
 */
const useSortOptionParam = ({ fields, defaultSortBy, defaultSortOrder, key = 'ordering' }) => {
  const allowed = useMemo(() => fields.flatMap((field) => [field, `-${field}`]), [fields]);
  const defaultOrdering = sortParam({ sortBy: defaultSortBy, sortOrder: defaultSortOrder });

  const [ordering, setOrdering] = useUrlParamState(key, defaultOrdering, allowed);

  const sortOption = useMemo(() => {
    const isDesc = ordering.startsWith('-');
    return { sortBy: isDesc ? ordering.slice(1) : ordering, sortOrder: isDesc ? -1 : 1 };
  }, [ordering]);

  const setSortOption = useCallback(
    (sortBy, sortOrder) => setOrdering(sortParam({ sortBy, sortOrder })),
    [setOrdering],
  );

  return [sortOption, setSortOption];
};

export default useSortOptionParam;
